import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Order {
  id: string
  order_number: string
  customer_name: string
  customer_email: string
  channel: string
  status: string
  payment_status: string
  total: number
  currency: string
  items_count: number
  shipping_method: string
  priority: string
  created_at: string
}

interface Queue {
  id: string
  name: string
  description: string
  count: number
  avg_wait_time_hours: number
  sla_hours: number
}

export default function OrderProcessingPage() {
  const [orders, setOrders] = useState<Order[]>([])
  const [queues, setQueues] = useState<Queue[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [ordersRes, queuesRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/orders/'),
      fetch(API_BASE + '/orders/queues/list'),
      fetch(API_BASE + '/orders/statistics')
    ])
    if (ordersRes.ok) setOrders((await ordersRes.json()).orders || [])
    if (queuesRes.ok) setQueues((await queuesRes.json()).queues || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered': return 'bg-green-100 text-green-800'
      case 'shipped': return 'bg-blue-100 text-blue-800'
      case 'packed': return 'bg-indigo-100 text-indigo-800'
      case 'processing': return 'bg-yellow-100 text-yellow-800'
      case 'pending': return 'bg-gray-100 text-gray-800'
      case 'cancelled': return 'bg-red-100 text-red-800'
      case 'returned': return 'bg-purple-100 text-purple-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'text-red-600'
      case 'high': return 'text-orange-600'
      case 'normal': return 'text-gray-600'
      default: return 'text-gray-400'
    }
  }

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Order Processing</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Create Order</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Orders Today</div>
              <div className="text-2xl font-bold">{statistics.orders_today}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Revenue Today</div>
              <div className="text-2xl font-bold text-green-600">{formatCurrency(statistics.revenue_today)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Pending</div>
              <div className="text-2xl font-bold text-yellow-600">{statistics.pending}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Processing</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.processing}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Avg Fulfillment</div>
              <div className="text-2xl font-bold">{statistics.avg_fulfillment_hours}h</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-6 gap-4 mb-8">
          {queues.map(queue => (
            <div key={queue.id} className="bg-white rounded-lg shadow p-4">
              <div className="font-semibold text-sm mb-1">{queue.name}</div>
              <div className="text-3xl font-bold text-blue-600 mb-1">{queue.count}</div>
              <div className="text-xs text-gray-500">
                Avg wait: {queue.avg_wait_time_hours}h
                {queue.avg_wait_time_hours > queue.sla_hours && (
                  <span className="text-red-600 ml-1">(SLA breach!)</span>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Recent Orders</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {orders.slice(0, 10).map(order => (
                  <tr key={order.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedOrder(order)}>
                    <td className="px-6 py-4">
                      <div className="font-medium flex items-center gap-2">
                        <span className={getPriorityColor(order.priority)}>●</span>
                        {order.order_number}
                      </div>
                      <div className="text-xs text-gray-500">{order.items_count} items • {order.channel}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">{order.customer_name}</div>
                      <div className="text-xs text-gray-500">{order.customer_email}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium">{formatCurrency(order.total, order.currency)}</div>
                      <div className={`text-xs ${order.payment_status === 'paid' ? 'text-green-600' : 'text-yellow-600'}`}>
                        {order.payment_status}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(order.status)}`}>
                        {order.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {selectedOrder && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold">{selectedOrder.order_number}</h3>
                <span className={`px-2 py-1 rounded text-xs ${getStatusColor(selectedOrder.status)}`}>
                  {selectedOrder.status}
                </span>
              </div>

              <div className="space-y-4">
                <div>
                  <div className="text-sm text-gray-500">Customer</div>
                  <div className="font-medium">{selectedOrder.customer_name}</div>
                  <div className="text-sm text-gray-500">{selectedOrder.customer_email}</div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-green-50 rounded">
                    <div className="text-2xl font-bold text-green-600">{formatCurrency(selectedOrder.total, selectedOrder.currency)}</div>
                    <div className="text-xs text-gray-500">Order Total</div>
                  </div>
                  <div className="p-3 bg-blue-50 rounded">
                    <div className="text-2xl font-bold text-blue-600">{selectedOrder.items_count}</div>
                    <div className="text-xs text-gray-500">Items</div>
                  </div>
                </div>

                <div>
                  <div className="text-sm text-gray-500">Shipping</div>
                  <div className="font-medium capitalize">{selectedOrder.shipping_method}</div>
                </div>

                <div>
                  <div className="text-sm text-gray-500">Channel</div>
                  <div className="font-medium capitalize">{selectedOrder.channel.replace(/_/g, ' ')}</div>
                </div>

                <div className="text-xs text-gray-500">
                  Created: {new Date(selectedOrder.created_at).toLocaleString()}
                </div>

                <div className="flex gap-2 pt-4">
                  <button className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">
                    Process
                  </button>
                  <button className="flex-1 px-3 py-2 border text-sm rounded hover:bg-gray-50">
                    View Details
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
